import type React from "react";

import { isCustomerRole, type RequestTypeEnum } from "@nlc/shared-types";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Controller, FormProvider } from "react-hook-form";

import { Button } from "@/components/button";
import {
  Checkbox,
  Checkboxes,
  CheckboxField,
  CheckboxGroup,
} from "@/components/checkbox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
} from "@/components/dialog";
import { Modal } from "@/components/modal";
import { NumberField } from "@/components/number-field";
import { RoleGuard } from "@/features/auth/permissions/role-guard";
import {
  handleDuplicatePastRequest,
  useDuplicatePastRequestForm,
} from "@/features/request/duplicate-past-request-modal/useDuplicatePastRequestForm";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import { useDuplicateTemplate } from "@/features/templates/hooks/useDuplicateTemplate.tsx";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";
import { useIsTemplate } from "@/hooks/useIsTemplate.tsx";
import { useUser } from "@/hooks/useUser";

interface SubmitWarningModalProps {
  isOpen: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
}

export function DuplicateRequestModal({
  isOpen,
  onOpenChange,
}: SubmitWarningModalProps) {
  const { getValues } = useEditRequestFormContext();

  const navigate = useNavigate();
  const form = useDuplicatePastRequestForm();
  const { data: request } = useGetRequest();
  const { data: user } = useUser();
  const { isTemplate } = useIsTemplate();
  const { handleCreateDraft } = useDuplicateTemplate();

  useEffect(() => {
    if (isOpen && request?.requestType) {
      const isCustomer = !!user && isCustomerRole(user.user.roleId);

      form.reset({
        includeAttachments: false,
        includeCollaborators: false,
        includeLeadershipNotes: !isCustomer,
        numberOfDuplicates: 1,
        requestId: request.id,
        requestType: request.requestType as RequestTypeEnum,
      });
    }
  }, [isOpen, request, user]);

  return (
    <Modal
      classNames={{ modal: "w-[400px]" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={(open) => {
        onOpenChange(open);
      }}
    >
      <Dialog>
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit((data) => {
              if (isTemplate) {
                handleCreateDraft();

                return;
              }
              navigate({
                // @ts-expect-error-providing-params-just-before-calling-duplicate-function
                search: (prev) => ({
                  ...prev,
                  "project-id": request?.projectId,
                }),
              });
              handleDuplicatePastRequest(data, navigate);
              onOpenChange(false);
            })}
          >
            <DialogBody>
              <div className="flex flex-col gap-xs">
                <span className="text-lg font-semibold text-text-primary">
                  Duplicate request
                </span>
                <DialogCloseButton />
                <span className="text-sm text-text-tertiary">
                  You are about to duplicate{" "}
                  <span className="text-text-secondary font-medium">
                    {getValues("name")}
                  </span>
                  . Do you want to include:{" "}
                </span>
              </div>
              <CheckboxGroup>
                <Checkboxes>
                  <CheckboxField>
                    <Controller
                      control={form.control}
                      name="includeAttachments"
                      render={({ field: { onChange, value } }) => (
                        <Checkbox
                          isSelected={value}
                          onChange={onChange}
                          value="attachments"
                        >
                          Attachments
                        </Checkbox>
                      )}
                    />
                  </CheckboxField>
                  <CheckboxField>
                    <Controller
                      control={form.control}
                      name="includeCollaborators"
                      render={({ field: { onChange, value } }) => (
                        <Checkbox
                          isSelected={value}
                          onChange={onChange}
                          value="collaborators"
                        >
                          Collaborators
                        </Checkbox>
                      )}
                    />
                  </CheckboxField>
                </Checkboxes>
              </CheckboxGroup>
              <RoleGuard allowedRoles={["team-leader", "super-admin"]}>
                <CheckboxField>
                  <Controller
                    control={form.control}
                    name="includeLeadershipNotes"
                    render={({ field: { onChange, value } }) => (
                      <Checkbox isSelected={value} onChange={onChange}>
                        Request Notes
                      </Checkbox>
                    )}
                  />
                </CheckboxField>
              </RoleGuard>
              <RoleGuard allowedRoles={["team-leader", "super-admin"]}>
                <Controller
                  control={form.control}
                  name="numberOfDuplicates"
                  render={({ field: { onChange, value } }) => (
                    <NumberField
                      className="w-1/2 mt-2"
                      label="Number of duplicates"
                      onChange={onChange}
                      value={value}
                    />
                  )}
                />
              </RoleGuard>
            </DialogBody>
            <DialogFooter className="flex gap-md">
              <Button
                className="flex-1"
                color="gray"
                onPress={() => onOpenChange(false)}
                size="lg"
                variant="secondary"
              >
                Nevermind
              </Button>
              <Button
                className="flex-1"
                isDisabled={form.formState.isSubmitting}
                size="lg"
                type="submit"
                variant="primary"
              >
                {form.formState.isSubmitting ? "Duplicating..." : "Duplicate"}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </Dialog>
    </Modal>
  );
}
